import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { clearSeoCache } from "@/lib/seo";

const PAGES = [["home", "Home page", "/"], ["tour", "Product tour", "/tour"], ["login", "Log in", "/login"], ["signup", "Sign up", "/signup"], ["join", "Staff app join pages", "/join/…"]];

const Field = ({ label, value, onChange, testId, long, max, hint }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between"><Label>{label}</Label>{max && <span className={`text-xs ${(value || "").length > max ? "text-rose-600" : "text-slate-400"}`}>{(value || "").length}/{max}</span>}</div>
    {long ? <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} /> : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const GooglePreview = ({ title, description, path }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="seo-google-preview">
    <p className="text-xs text-emerald-700">{window.location.host}{path}</p>
    <p className="truncate text-base text-blue-700">{title}</p>
    <p className="line-clamp-2 text-xs text-slate-600">{description}</p>
  </div>
);

const previewTitle = (k, title, site, fallback) => {
  const own = (title || "").replace(/\{business\}/g, "Your Business") || (k === "join" ? "Your Business staff app" : "");
  return own ? (own.includes(site) ? own : `${own} | ${site}`) : fallback || site;
};

export const SeoEditor = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/super/cms", { params: { key: "seo" } }).then((r) => setS({ pages: {}, ...r.data }));
  }, []);
  if (!s) return null;
  const setPage = (k, patch) => setS({ ...s, pages: { ...s.pages, [k]: { ...(s.pages[k] || {}), ...patch } } });
  const save = async () => {
    try {
      await api.put("/super/cms", { content: s }, { params: { key: "seo" } });
      clearSeoCache();
      toast.success("SEO and sharing settings published");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6" data-testid="seo-editor">
      <div className="flex justify-end"><Button className="rounded-full" onClick={save} data-testid="seo-save-btn">Publish changes</Button></div>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-lg font-semibold">Whole site</h3>
        <Field label="Site name" value={s.site_name} onChange={(v) => setS({ ...s, site_name: v })} testId="seo-site-name" hint="Added after each page title, e.g. Product tour | StaffRotas" />
        <Field label="Default page title" value={s.default_title} onChange={(v) => setS({ ...s, default_title: v })} testId="seo-default-title" max={60} />
        <Field label="Default description" value={s.default_description} onChange={(v) => setS({ ...s, default_description: v })} testId="seo-default-description" long max={160} />
        <Field label="Default share image URL" value={s.default_image} onChange={(v) => setS({ ...s, default_image: v })} testId="seo-default-image" hint="Shown when a link is shared on WhatsApp, Facebook, LinkedIn or X. 1200×630 works best." />
      </div>
      {PAGES.map(([k, label, path]) => {
        const p = s.pages[k] || {};
        return (
          <div key={k} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid={`seo-page-${k}`}>
            <h3 className="font-display text-lg font-semibold">{label} <span className="font-mono text-xs font-normal text-slate-400">{path}</span></h3>
            <Field label="Page title" value={p.title} onChange={(v) => setPage(k, { title: v })} testId={`seo-${k}-title`} max={60} hint={k === "join" ? "Use {business} for the business name, e.g. {business} team app. Leave empty for '{business} staff app'" : undefined} />
            <Field label="Meta description" value={p.description} onChange={(v) => setPage(k, { description: v })} testId={`seo-${k}-description`} long max={160} />
            <Field label="Share image URL (optional)" value={p.image} onChange={(v) => setPage(k, { image: v })} testId={`seo-${k}-image`} />
            <GooglePreview title={previewTitle(k, p.title, s.site_name || "StaffRotas", s.default_title)} description={p.description || s.default_description} path={path} />
          </div>
        );
      })}
    </div>
  );
};
